import { IconCheck, IconMessageCircle } from "@tabler/icons-react";
import { useState, type FormEvent } from "react";
import { api, ApiError, type RequestKind } from "../api";
import { useSession } from "../session";
import { Card, ErrorNote } from "./ui";

/** "Request a change" and "request a cancellation" (docs/16 decision: view and pay only; changes are a request to the consultant). */
export function RequestsCard({ tripId, holder }: { tripId: string; holder: boolean }) {
  const { t } = useSession();
  const [kind, setKind] = useState<RequestKind>("change");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.tripRequest(tripId, { kind, text: body });
      setSent(true);
      setText("");
    } catch (err) {
      if (err instanceof ApiError && err.code === "holder_only") setError(t("errHolderOnlyRequest"));
      else setError(t("somethingWrong"));
    } finally {
      setBusy(false);
    }
  };

  if (!holder) {
    return (
      <Card className="requests-card">
        <p className="small">{t("requestsBody")}</p>
        <p className="muted small">{t("errHolderOnlyRequest")}</p>
      </Card>
    );
  }

  if (sent) {
    return (
      <Card className="requests-card requests-sent">
        <p className="saved">
          <IconCheck size={18} /> <strong>{t("requestSent")}</strong>
        </p>
        <p className="small">{t("requestSentBody")}</p>
        <div className="row-buttons">
          <a href="/conversation" className="button button-small button-primary">
            <IconMessageCircle size={16} /> {t("openConversation")}
          </a>
          <button type="button" className="link-button" onClick={() => setSent(false)}>
            {t("newRequest")}
          </button>
        </div>
      </Card>
    );
  }

  return (
    <form className="card requests-card form" onSubmit={(e) => void submit(e)}>
      <p className="small">{t("requestsBody")}</p>
      <div className="segmented" role="radiogroup" aria-label={t("requests")}>
        <button type="button" role="radio" aria-checked={kind === "change"} className={kind === "change" ? "on" : ""} onClick={() => setKind("change")}>
          {t("requestChange")}
        </button>
        <button type="button" role="radio" aria-checked={kind === "cancellation"} className={kind === "cancellation" ? "on" : ""} onClick={() => setKind("cancellation")}>
          {t("requestCancellation")}
        </button>
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={kind === "change" ? t("requestChangePlaceholder") : t("requestCancelPlaceholder")} rows={3} maxLength={4000} required />
      <p className="muted small">{t("requestsNote")}</p>
      {error && <ErrorNote>{error}</ErrorNote>}
      <div>
        <button type="submit" className="button button-primary button-small" disabled={busy || !text.trim()}>
          {busy ? t("sending") : t("send")}
        </button>
      </div>
    </form>
  );
}
